// Drawer katmanları — ref_files/btys-erp-01-stok-karti.html'deki openDrawer/
// quickNew/closeDrawer davranışının birebir karşılığı.
//
// Referans markup:
//   1. seviye: .scrim(.on) + aside.drawer(.sm|.md)(.on)(.pushed)
//   2. seviye: .scrim.s2(.on) + aside.drawer.sm.l2(.on)
//   gövde:     .dr-h > div > .dr-t + .dr-s  ·  .dr-b  ·  .dr-f
//
// 2. seviye açılınca 1. seviye .pushed alır (geri itilir ama KAPANMAZ) —
// "işleminiz açık kalır" vaadi bu sınıfla görünür hale geliyor.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type DrawerBoyut = 'sm' | 'md' | 'lg'

export type DrawerTanim = {
  baslik: string
  altBaslik?: string
  boyut?: DrawerBoyut
  /**
   * Gövde ve footer'ı BİRLİKTE render eder — <c>DrawerGovde</c> ve
   * <c>DrawerAltlik</c> bileşenlerini kullanın. Tek parça olması, içeriğin
   * kendi durumunu (hook) tutabilmesi için gerekli: footer butonları çoğu
   * zaman gövdedeki form durumuna bakıyor.
   */
  icerik: ReactNode
}

/** Drawer gövdesi (.dr-b). */
export const DrawerGovde = ({ children }: { children: ReactNode }) => (
  <div className="dr-b">{children}</div>
)

/** Drawer alt şeridi (.dr-f) — buton alanı. */
export const DrawerAltlik = ({ children }: { children: ReactNode }) => (
  <div className="dr-f">{children}</div>
)

type Katman = DrawerTanim & { id: number }

type Baglam = {
  ac: (tanim: DrawerTanim) => number
  kapat: (id?: number) => void
  katmanlar: Katman[]
}

const DrawerBaglami = createContext<Baglam | null>(null)

let sonrakiId = 1

export function DrawerSaglayici({ children }: { children: ReactNode }) {
  const [katmanlar, setKatmanlar] = useState<Katman[]>([])

  const ac = useCallback((tanim: DrawerTanim) => {
    const id = sonrakiId++
    // En çok iki seviye: referansta drawer ve drawer2 var, üçüncüsü yok.
    setKatmanlar((k) => [...k.slice(-1), { ...tanim, id }])
    return id
  }, [])

  const kapat = useCallback((id?: number) => {
    setKatmanlar((k) => (id === undefined ? k.slice(0, -1) : k.filter((x) => x.id !== id)))
  }, [])

  // ESC daima EN ÜSTTEKİNİ kapatır (referans: önce drawer2, sonra drawer).
  useEffect(() => {
    if (katmanlar.length === 0) return
    const dinle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') kapat()
    }
    document.addEventListener('keydown', dinle)
    return () => document.removeEventListener('keydown', dinle)
  }, [katmanlar.length, kapat])

  const deger = useMemo<Baglam>(() => ({ ac, kapat, katmanlar }), [ac, kapat, katmanlar])

  return <DrawerBaglami.Provider value={deger}>{children}</DrawerBaglami.Provider>
}

export function useDrawer() {
  const b = useContext(DrawerBaglami)
  if (!b) throw new Error('useDrawer yalnız DrawerSaglayici içinde kullanılabilir.')
  return { ac: b.ac, kapat: b.kapat, derinlik: b.katmanlar.length }
}

export function DrawerYuvasi() {
  const b = useContext(DrawerBaglami)
  if (!b) return null

  return (
    <>
      {b.katmanlar.map((k, i) => {
        const ikinci = i === 1
        const itilmis = i === 0 && b.katmanlar.length > 1
        return (
          <div key={k.id}>
            <div
              className={`scrim${ikinci ? ' s2' : ''} on`}
              onClick={() => b.kapat(k.id)}
            />
            <aside
              className={[
                'drawer',
                k.boyut ?? '',
                ikinci ? 'l2' : '',
                itilmis ? 'pushed' : '',
                'on',
              ].filter(Boolean).join(' ')}
              role="dialog"
              aria-modal="true"
              aria-label={k.baslik}
            >
              <div className="dr-h">
                <div>
                  <div className="dr-t">{k.baslik}</div>
                  {k.altBaslik && <div className="dr-s">{k.altBaslik}</div>}
                </div>
                <button
                  className="icon-btn dr-x"
                  type="button"
                  onClick={() => b.kapat(k.id)}
                  title="Kapat"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {k.icerik}
            </aside>
          </div>
        )
      })}
    </>
  )
}
